import React, { useCallback, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { intersectionWith, unionWith, maxBy, size, cloneDeep } from 'lodash';
import { useMusicArtist } from '../useMusicArtist';
import { Slider, Typography, FormControlLabel, Checkbox } from '@mui/material';

const Graph = ({ data, dimensions }) => {
    // console.log(`Running Graph with...`);
    // console.log(data);
    // width: 1200
    // height: 800
    const svgRef = React.useRef(null);
    const { width, height, margin = {} } = dimensions;
    // const svgWidth = width + margin.left + margin.right;
    // const svgHeight = height + margin.top + margin.bottom;

    const [graphData, setGraphData] = useState({
        ...data,
        numCollabs: 1,
        showLeafLabels: false
    });
    const [selectedArtist, setSelectedArtist] = useState('');

    const maxNumTitles = Math.max(
        ...graphData.links.map((link) => link.titles.length)
    );

    const { data: collabData } = useMusicArtist(selectedArtist);

    const dblclickHandler = (event, d) => {
        console.log(`Retrieving collaborations for ${d.name}`);
        setSelectedArtist(d);
    };
    const color = d3.scaleOrdinal(d3.schemeCategory10);
    const linkWidthScale = d3.scaleLinear().range([1, 10]);
    const nodeSizeScale = d3.scaleLinear().range([5, 20]);
    const linkDistanceScale = d3.scaleLinear().range([70, 30]);

    const clamp = (x, lo, hi) => {
        return x < lo ? lo : x > hi ? hi : x;
    };

    const onSliderChange = (event, value) => {
        // console.log(value);
        const newGraphData = cloneDeep(graphData);
        const whitelistLinks = newGraphData.links.filter(
            (link) => link.titles.length >= value
        );
        const whitelistNodes = new Set(
            whitelistLinks.flatMap((link) => [link.source, link.target])
        );
        newGraphData.links = newGraphData.links.map((link) => {
            link.disabled =
                whitelistNodes.has(link.target) &&
                whitelistNodes.has(link.source)
                    ? false
                    : true;
            return link;
        });
        newGraphData.nodes = newGraphData.nodes.map((node) => {
            node.disabled = whitelistNodes.has(node.id) ? false : true;
            return node;
        });
        newGraphData.numCollabs = value;
        setGraphData(newGraphData);
    };

    const onCheckboxChange = (event) => {
        setGraphData({ ...graphData, showLeafLabels: event.target.checked });
    };

    useEffect(() => {
        if (collabData) {
            console.log('Adding selected artist collaborations to graph');
            const currentIds = new Set(graphData.nodes.map((node) => node.id));
            const intersection = collabData.nodes
                .filter((node) => currentIds.has(node.id))
                .filter((x) => x.degree > 1);

            const nodes = unionWith(
                intersection,
                graphData.nodes,
                collabData.nodes,
                (a, b) => a.id === b.id
            ); // console.log(intersection);

            const newGraphData = {
                nodes: nodes,
                links: unionWith(
                    collabData.links,
                    graphData.links,
                    (a, b) => a.source === b.source && a.target === b.target
                ),
                numCollabs: graphData.numCollabs
            };

            setGraphData(newGraphData);
        }
    }, [collabData]);

    useEffect(() => {
        console.log('Generating collaborations graph');

        const svg = d3
            .select(svgRef.current)
            .attr('preserveAspectRatio', 'xMinYMin meet')
            .attr('viewBox', [-width / 4, -height / 2, width, height])
            .classed('svg-content', true);

        const { nodes, links, numCollabs, showLeafLabels } =
            cloneDeep(graphData);

        console.log(`Number of nodes: ${nodes.length}`);
        console.log(`Number of links: ${links.length}`);
        console.debug({ nodes });
        console.debug({ links });
        console.debug({ numCollabs });

        const min_num_titles = 1;
        const max_num_titles = size(
            maxBy(links, (link) => {
                // console.log({titles: size(link.titles)})
                return size(link.titles);
            }).titles
        );

        const minDegree = 1;
        const maxDegree = Math.max(...nodes.map((node) => node.degree));

        linkWidthScale.domain([min_num_titles, max_num_titles]);
        nodeSizeScale.domain([minDegree, maxDegree]);
        linkDistanceScale.domain([min_num_titles, max_num_titles]);

        console.debug({ min_num_titles, max_num_titles });

        const everything = svg.selectAll('*');
        everything.remove();
        d3.select('.tooltip').remove();

        const tooltip = d3
            .select('body')
            .append('div')
            .attr('class', 'tooltip');

        const forceLink = d3
            .forceLink(links)
            .id((d) => d.id)
            .distance((d) => linkDistanceScale(d.titles.length));

        const simulation = d3
            .forceSimulation(nodes)
            .force('link', forceLink)
            .force('charge', d3.forceManyBody())
            .force('center', d3.forceCenter());

        const drag = d3
            .drag()
            .on('start', () => {
                d3.select(this).classed('fixed', true);
            })
            .on('drag', (event, d) => {
                d.fx = clamp(event.x, 0, width);
                d.fy = clamp(event.y, 0, height);
                simulation.alpha(1).restart();
            });

        const link = svg
            .append('g')
            .attr('stroke', 'white')
            .attr('stroke-opacity', '50%')
            .selectAll('line')
            .data(links)
            .join('line')
            .attr('visibility', (d) => (d.disabled ? 'hidden' : 'visible'))
            .attr('stroke-width', (d) => linkWidthScale(size(d.titles)) + 'px')
            .on('mouseover', () => tooltip.style('visibility', 'visible'))
            .on('mouseout', () => tooltip.style('visibility', 'hidden'))
            .on('mousemove', function (event, d) {
                return tooltip
                    .style('top', event.pageY + 30 + 'px')
                    .style('left', event.pageX + 20 + 'px').html(`
                      <div>
                          <p>Titles: ${d.titles.join(',')}</p>
                          <p>Artist: ${d.source.name}, ${d.target.name}<p>
                      </div>`);
            });

        const node = svg
            .selectAll('.node')
            .data(nodes)
            .enter()
            .append('g')
            .attr('class', 'node')
            .attr('visibility', (d) => (d.disabled ? 'hidden' : 'visible'))
            .call(drag);

        node.append('circle')
            .attr('r', (d) => nodeSizeScale(d.degree))
            .attr('fill', (d) => (d.selected ? color(1) : color(0)))
            .attr('fill-opacity', (d) => (d.selected ? 1 : 0.7))
            .attr('cx', 0)
            .attr('cy', 0)
            .on('dblclick', dblclickHandler)
            .on('mouseover', () => tooltip.style('visibility', 'visible'))
            .on('mouseout', () => tooltip.style('visibility', 'hidden'))
            .on('mousemove', function (event, d) {
                return tooltip
                    .style('top', event.pageY - 10 + 'px')
                    .style('left', event.pageX + 20 + 'px')
                    .html(`<span>${d.name}</span>`);
            });

        // const labels
        node.append('text')
            .attr('class', (d) => (d.selected ? 'label selected' : 'label'))
            .attr('transform', (d) => {
                const r = nodeSizeScale(d.degree);
                return `translate(${r + 5}, ${r / 2})`;
            })
            .text((d) => (d.selected || showLeafLabels ? d.name : ''));

        node.call(drag);
        // .on('click', (event, d) => {
        //     delete d.fx;
        //     delete d.fy;
        //     d3.select(this).classed('fixed', false);
        //     simulation.alpha(1).restart();
        // });

        simulation.on('tick', () => {
            link.attr('x1', (d) => d.source.x)
                .attr('y1', (d) => d.source.y)
                .attr('x2', (d) => d.target.x)
                .attr('y2', (d) => d.target.y);

            node.attr('transform', (d) => `translate(${d.x},${d.y})`);
            // node
            // .attr("cx", d => d.x)
            // .attr("cy", d => d.y);
            
        });
    }, [graphData]);

    return (
        <div className="graph-container">
            <div className="graph-container-header">
                <Slider
                    sx={{
                        'margin-top': '40px'
                    }}
                    aria-label="Number of Titles"
                    defaultValue={1}
                    onChange={onSliderChange}
                    // getAriaValueText={valuetext}
                    // size="small"
                    valueLabelDisplay="on"
                    step={1}
                    marks
                    min={1}
                    max={maxNumTitles}
                />
                <Typography gutterBottom sx={{ color: 'white' }}>
                    Number of Collborations
                </Typography>
                <FormControlLabel
                    sx={{ color: 'white' }}
                    control={
                        <Checkbox
                            sx={{ color: 'white' }}
                            onChange={onCheckboxChange}
                        />
                    }
                    label="Show leaf labels"
                />
            </div>
            <div className="svg-container">
                <svg ref={svgRef} />
            </div>
        </div>
    );
};

export default Graph;
